import React from 'react';

import { CONTACT_EMAIL } from '@/config/site';
import { openConsentSettings } from '@/lib/consent';

// Update this date whenever the policy text changes
const PRIVACY_POLICY_LAST_UPDATED = '2026. szeptember 27.';

const H3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-lg font-bold text-white mt-8 mb-3 uppercase tracking-wide">{children}</h3>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-neutral-300 leading-relaxed my-3">{children}</p>
);

const UL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul className="list-disc pl-6 my-3 space-y-1.5 text-neutral-300 marker:text-neon-red">
    {children}
  </ul>
);

const A: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-neon-red underline hover:text-white transition-colors"
  >
    {children}
  </a>
);

interface ProcessingProps {
  purpose: string;
  data: string;
  legalBasis: string;
  retention: string;
  processor?: React.ReactNode;
}

const Processing: React.FC<ProcessingProps> = ({
  purpose,
  data,
  legalBasis,
  retention,
  processor,
}) => (
  <dl className="my-4 border border-neutral-800 bg-black/40 text-sm divide-y divide-neutral-800">
    {[
      ['Cél', purpose],
      ['Kezelt adatok', data],
      ['Jogalap', legalBasis],
      ['Időtartam', retention],
      ...(processor ? [['Adatfeldolgozó / címzett', processor] as const] : []),
    ].map(([label, value]) => (
      <div
        key={label as string}
        className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-1 sm:gap-4 p-3"
      >
        <dt className="font-mono uppercase tracking-wider text-xs text-neutral-400">{label}</dt>
        <dd className="text-neutral-300">{value}</dd>
      </div>
    ))}
  </dl>
);

const StorageTable: React.FC<{ rows: [string, string, string, string][] }> = ({ rows }) => (
  <div className="my-4 overflow-x-auto">
    <table className="w-full text-sm border border-neutral-800">
      <thead className="bg-neutral-950 text-neutral-400 font-mono uppercase text-xs tracking-wider">
        <tr>
          <th className="text-left p-2 border-b border-neutral-800">Név</th>
          <th className="text-left p-2 border-b border-neutral-800">Típus</th>
          <th className="text-left p-2 border-b border-neutral-800">Cél</th>
          <th className="text-left p-2 border-b border-neutral-800">Lejárat</th>
        </tr>
      </thead>
      <tbody className="text-neutral-300">
        {rows.map(([name, type, purpose, expiry]) => (
          <tr key={name} className="border-b border-neutral-800 last:border-b-0 align-top">
            <td className="p-2 font-mono text-xs break-all">{name}</td>
            <td className="p-2">{type}</td>
            <td className="p-2">{purpose}</td>
            <td className="p-2">{expiry}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const PrivacyPolicyContent: React.FC = () => (
  <div className="font-body text-base">
    <p className="font-mono text-xs uppercase tracking-widest text-neutral-400">
      Hatályos: {PRIVACY_POLICY_LAST_UPDATED}
    </p>

    <H3>1. Az adatkezelő</H3>
    <UL>
      <li>
        <strong className="text-white">Név:</strong> Hard News Hungary
      </li>
      <li>
        <strong className="text-white">Weboldal:</strong> https://hardnewshungary.com
      </li>
      <li>
        <strong className="text-white">E-mail:</strong>{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-neon-red underline hover:text-white">
          {CONTACT_EMAIL}
        </a>
      </li>
    </UL>
    <P>
      Adatvédelmi kérdésekben, illetve jogaid gyakorlásához a fenti e-mail-címen érsz el minket.
    </P>

    <H3>2. Jogszabályi háttér</H3>
    <P>Az adatkezelés során az alábbi jogszabályok szerint járunk el:</P>
    <UL>
      <li>
        az Európai Parlament és a Tanács (EU) 2016/679 rendelete az általános adatvédelmi
        rendeletről (GDPR);
      </li>
      <li>
        az információs önrendelkezési jogról és az információszabadságról szóló 2011. évi CXII.
        törvény (Infotv.);
      </li>
      <li>
        az elektronikus hírközlésről szóló 2003. évi C. törvény (Eht.) 155. § (4) bekezdése (sütik,
        a készüléken történő tárolás).
      </li>
    </UL>

    <H3>3. Milyen adatokat, miért és meddig kezelünk?</H3>
    <P>
      A weboldal regisztráció nélkül használható, felhasználói fiókot nem hozunk létre, és
      hírlevelet sem küldünk. Az alábbi esetekben kezelünk személyes adatot.
    </P>

    <h4 className="text-white font-bold mt-6">3.1. A weboldal üzemeltetése, biztonsága</h4>
    <Processing
      purpose="A weboldal kiszolgálása, a hibák felderítése és a visszaélések (pl. túlterheléses támadás, túl sok kérés) megelőzése."
      data="IP-cím, a kérés időpontja, a megnyitott oldal címe, böngésző és operációs rendszer típusa (user agent), hivatkozó oldal."
      legalBasis="Jogos érdek, GDPR 6. cikk (1) f) pont – a weboldal biztonságos és folyamatos működése."
      retention="A tárhelyszolgáltató naplóiban legfeljebb 30 napig; a kérésszám-korlátozáshoz használt IP-címet csak átmenetileg, a memóriában, legfeljebb 1 óráig tároljuk."
      processor={
        <>
          Vercel Inc. (USA) – tárhely és tartalomkézbesítés.{' '}
          <A href="https://vercel.com/legal/privacy-policy">Adatvédelmi tájékoztató</A>
        </>
      }
    />

    <h4 className="text-white font-bold mt-6">
      3.2. Látogatottsági statisztika (Google Analytics)
    </h4>
    <Processing
      purpose="Anonim, összesített statisztika arról, mely cikkeket és oldalakat olvassák, milyen eszközről, hogy a tartalmat és az oldal működését fejleszthessük (a betöltési sebesség mérését is beleértve)."
      data="Véletlenszerű azonosító (sütiben), a megtekintett oldalak, az oldalon töltött idő, eszköz- és böngészőadatok, hozzávetőleges földrajzi hely (ország/város). A Google Analytics 4 az IP-címet nem tárolja."
      legalBasis="Hozzájárulás, GDPR 6. cikk (1) a) pont és Eht. 155. § (4). A Google Analytics csak az „Elfogadom” gomb megnyomása után töltődik be; hozzájárulás nélkül semmilyen adat nem jut el a Google-hoz."
      retention="A sütik legfeljebb 2 évig élnek; az eseményadatokat a Google a beállított megőrzési idő (legfeljebb 14 hónap) leteltével törli. A hozzájárulás visszavonásakor a sütiket töröljük."
      processor={
        <>
          Google Ireland Ltd. (Gordon House, Barrow Street, Dublin 4, Írország); az adatok az USA-ba
          is továbbításra kerülhetnek (lásd 5. pont).{' '}
          <A href="https://policies.google.com/privacy?hl=hu">Adatvédelmi tájékoztató</A>
        </>
      }
    />
    <P>
      A hozzájárulásodat bármikor, indoklás nélkül visszavonhatod vagy módosíthatod az oldal alján
      található{' '}
      <button
        type="button"
        onClick={openConsentSettings}
        className="text-neon-red underline hover:text-white transition-colors"
      >
        Süti beállítások
      </button>{' '}
      linkkel. A visszavonás nem érinti a korábbi adatkezelés jogszerűségét.
    </P>

    <h4 className="text-white font-bold mt-6">3.3. Hibakövetés (Sentry)</h4>
    <Processing
      purpose="Az oldal működése közben fellépő programhibák észlelése és javítása."
      data="Hibaüzenet és a hiba technikai körülményei (megnyitott oldal, böngésző és operációs rendszer típusa, időpont). Nevet, e-mail-címet vagy más, a látogatót közvetlenül azonosító adatot nem gyűjtünk."
      legalBasis="Jogos érdek, GDPR 6. cikk (1) f) pont – a hibamentes működés biztosítása."
      retention="A szolgáltató beállításai szerint, legfeljebb 90 napig."
      processor={
        <>
          Functional Software, Inc. (Sentry), USA.{' '}
          <A href="https://sentry.io/privacy/">Adatvédelmi tájékoztató</A>
        </>
      }
    />

    <h4 className="text-white font-bold mt-6">3.4. Kapcsolatfelvétel</h4>
    <Processing
      purpose="A kapcsolatfelvételi űrlapon vagy e-mailben küldött üzenetek (pl. sajtóanyag, eseménybeküldés, együttműködés) megválaszolása."
      data="Név, e-mail-cím, az üzenet tartalma és az általad megadott egyéb adatok."
      legalBasis="Hozzájárulás, GDPR 6. cikk (1) a) pont – az üzenet elküldésével adod meg."
      retention="Az ügy lezárását követő legfeljebb 1 évig, illetve a hozzájárulás visszavonásáig."
      processor={<>Az űrlap: Tally BV (Belgium); e-mail: Google Ireland Ltd. (Gmail).</>}
    />

    <H3>4. Sütik és a készüléken tárolt adatok</H3>
    <P>
      Az oldal működéséhez szükséges adatokat a böngésződ helyi tárolójában (localStorage) tartjuk,
      ezekhez nem kell hozzájárulás, és nem kerülnek el hozzánk. Statisztikai sütit csak a
      hozzájárulásoddal helyezünk el. Hirdetési vagy követő sütit nem használunk.
    </P>
    <StorageTable
      rows={[
        [
          'hnh-cookie-consent',
          'Szükséges (localStorage)',
          'Megjegyzi, hogy elfogadtad vagy elutasítottad a statisztikai sütiket.',
          'Törlésig',
        ],
        [
          'popup_banner_last_shown',
          'Szükséges (localStorage)',
          'Hogy a felugró ajánló naponta legfeljebb egyszer jelenjen meg.',
          'Törlésig',
        ],
        [
          'pwa-install-dismissed',
          'Szükséges (localStorage)',
          'Megjegyzi, ha bezártad az alkalmazás telepítésére vonatkozó ajánlatot.',
          'Törlésig',
        ],
        [
          'Service worker gyorsítótár',
          'Szükséges (böngésző gyorsítótár)',
          'Gyorsabb betöltés és offline oldal; csak az oldal saját fájljait és a cikkeket tárolja.',
          'Legfeljebb 1 óra, illetve frissítésig',
        ],
        [
          '_ga, _ga_<azonosító>',
          'Statisztikai süti (hozzájárulással)',
          'Google Analytics: a látogatók megkülönböztetése.',
          '2 év',
        ],
      ]}
    />
    <P>A tárolt adatokat a böngésződ beállításaiban bármikor törölheted.</P>

    <H3>5. Külső szolgáltatások, adattovábbítás</H3>
    <P>
      Az oldal megjelenítéséhez külső szolgáltatók erőforrásait töltjük be. Ilyenkor a böngésződ
      közvetlenül kapcsolódik hozzájuk, így megkapják az IP-címedet és a böngésző adatait:
    </P>
    <UL>
      <li>
        <strong className="text-white">Contentful GmbH</strong> (Berlin, Németország) – a cikkek
        tartalma és képei.
      </li>
      <li>
        <strong className="text-white">Google Fonts</strong> (Google Ireland Ltd.) – betűtípusok.
      </li>
      <li>
        <strong className="text-white">Spotify AB</strong> (Stockholm, Svédország) – a cikkekbe és
        aloldalakba ágyazott lejátszók. A Spotify a saját szabályai szerint sütit helyezhet el;{' '}
        <A href="https://www.spotify.com/hu/legal/privacy-policy/">adatvédelmi tájékoztató</A>.
      </li>
      <li>
        <strong className="text-white">Tally BV</strong> (Belgium) – a kapcsolatfelvételi oldalon az
        űrlap betöltésekor.
      </li>
    </UL>
    <P>
      A közösségi oldalak (Facebook, Instagram, TikTok stb.) ikonjai és a megosztás gombok egyszerű
      hivatkozások: adat csak akkor jut el az adott szolgáltatóhoz, ha rájuk kattintasz.
    </P>
    <P>
      Egyes szolgáltatóink (Vercel, Google, Sentry) az Egyesült Államokban is kezelhetnek adatot. Az
      adattovábbítás az EU–USA adatvédelmi keretrendszer (Data Privacy Framework) szerinti
      megfelelőségi határozat, illetve az Európai Bizottság által elfogadott általános szerződési
      feltételek (GDPR 46. cikk) alapján történik.
    </P>
    <P>Személyes adatot nem adunk el, és marketingcélra harmadik félnek nem adunk át.</P>

    <H3>6. Adatbiztonság</H3>
    <P>
      Az oldal kizárólag titkosított (HTTPS) kapcsolaton érhető el. Szigorú biztonsági fejlécekkel
      (pl. Content Security Policy, HSTS) korlátozzuk, milyen külső forrásból tölthető be tartalom,
      a szerveroldali végpontokat kérésszám-korlátozás védi, és a szolgáltatóinkhoz csak a
      feladatához szükséges személyek férnek hozzá.
    </P>

    <H3>7. Jogaid</H3>
    <P>Az adataiddal kapcsolatban az alábbi jogok illetnek meg:</P>
    <UL>
      <li>
        <strong className="text-white">tájékoztatás és hozzáférés</strong> – megkérdezheted, milyen
        adatot kezelünk rólad (GDPR 15. cikk);
      </li>
      <li>
        <strong className="text-white">helyesbítés</strong> – kérheted a pontatlan adatok javítását
        (16. cikk);
      </li>
      <li>
        <strong className="text-white">törlés</strong> („elfeledtetéshez való jog”, 17. cikk);
      </li>
      <li>
        <strong className="text-white">az adatkezelés korlátozása</strong> (18. cikk);
      </li>
      <li>
        <strong className="text-white">adathordozhatóság</strong> (20. cikk);
      </li>
      <li>
        <strong className="text-white">tiltakozás</strong> a jogos érdeken alapuló adatkezelés ellen
        (21. cikk);
      </li>
      <li>
        <strong className="text-white">a hozzájárulás visszavonása</strong> bármikor, a korábbi
        adatkezelés jogszerűségének érintése nélkül (7. cikk (3) bekezdés).
      </li>
    </UL>
    <P>
      Kérésedre késedelem nélkül, de legfeljebb egy hónapon belül válaszolunk (GDPR 12. cikk (3)
      bekezdés). Mivel az oldal nem kér bejelentkezést, a legtöbb esetben nem tudunk olyan adatot
      hozzád kötni, amely név vagy e-mail-cím nélkül keletkezett (pl. statisztikai adatok).
    </P>

    <H3>8. Jogorvoslat</H3>
    <P>
      Ha úgy érzed, hogy megsértettük az adatvédelmi jogaidat, kérjük, először írj nekünk. Panaszt
      tehetsz a felügyeleti hatóságnál is:
    </P>
    <UL>
      <li>
        <strong className="text-white">
          Nemzeti Adatvédelmi és Információszabadság Hatóság (NAIH)
        </strong>
      </li>
      <li>Cím: 1055 Budapest, Falk Miksa utca 9–11.</li>
      <li>Postacím: 1363 Budapest, Pf. 9.</li>
      <li>Telefon: +36 1 391 1400</li>
      <li>
        E-mail:{' '}
        <a
          href="mailto:ugyfelszolgalat@naih.hu"
          className="text-neon-red underline hover:text-white"
        >
          ugyfelszolgalat@naih.hu
        </a>
      </li>
      <li>
        Weboldal: <A href="https://naih.hu">naih.hu</A>
      </li>
    </UL>
    <P>
      Jogaid megsértése esetén bírósághoz is fordulhatsz; a pert – választásod szerint – a
      lakóhelyed vagy tartózkodási helyed szerint illetékes törvényszék előtt is megindíthatod.
    </P>

    <H3>9. Kiskorúak</H3>
    <P>
      16 év alatti látogató a statisztikai sütikhez és a kapcsolatfelvételhez szükséges
      hozzájárulást csak szülője vagy törvényes képviselője beleegyezésével adhatja meg.
    </P>

    <H3>10. A tájékoztató módosítása</H3>
    <P>
      A tájékoztatót az adatkezelés vagy a jogszabályok változása esetén frissítjük. A mindenkori
      hatályos változat ezen az oldalon érhető el; a hatálybalépés dátumát a tájékoztató elején
      tüntetjük fel.
    </P>
  </div>
);
